import { formatCurrency as formatCurrencyShared } from '../../../utils/formatUtils';

/**
 * Obtiene la fecha actual en formato YYYY-MM-DD
 * @returns {string} - Fecha formateada
 */
export const getCurrentDate = () => {
  const dateNow = new Date();
  const year = dateNow.getFullYear();
  const monthWithOffset = dateNow.getUTCMonth() + 1;
  const month = monthWithOffset.toString().length < 2 
    ? `0${monthWithOffset}` 
    : monthWithOffset;
  const date = dateNow.getUTCDate().toString().length < 2 
    ? `0${dateNow.getUTCDate()}` 
    : dateNow.getUTCDate();

  return `${year}-${month}-${date}`;
};

/**
 * Formatea una fecha desde formato de base de datos a DD/MM/YYYY
 * @param {string} dateStr - Fecha en formato YYYY-MM-DD HH:mm:ss
 * @returns {string} - Fecha formateada DD/MM/YYYY
 */
export const formatDate = (dateStr) => {
  if (!dateStr) return '';
  
  const [year, month, day] = dateStr.split(' ')[0].split('-');
  return `${day}/${month}/${year}`;
};

/**
 * Calcula el interés entre dos fechas
 * @param {number} principal - Monto principal
 * @param {string} startDate - Fecha inicial
 * @param {string} endDate - Fecha final
 * @param {number} dailyRate - Tasa diaria (por defecto 0.3%)
 * @returns {number} - Interés calculado
 */
export const calculateInterest = (principal, startDate, endDate, dailyRate = 0.003) => {
  if (!principal || !startDate || !endDate) return 0;

  const firstDueDate = new Date(startDate);
  const thirdDueDate = new Date(endDate);
  const diffTime = Math.max(thirdDueDate - firstDueDate, 0);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return principal * dailyRate * diffDays;
};

/**
 * Formatea los cupones para envío a la API
 * @param {Array} originalCoupons - Cupones originales
 * @param {Array} modifiedCoupons - Cupones modificados
 * @param {Array} payChecks - Estados de checkboxes de pago
 * @param {Array} regularizeChecks - Estados de checkboxes de regularización
 * @returns {Object} - Datos formateados para la API
 */
export const formatCouponsForUpdate = (
  originalCoupons, 
  modifiedCoupons, 
  payChecks, 
  regularizeChecks
) => {
  const coupons = originalCoupons
    .map((coupon, index) => {
      if (payChecks[index] || regularizeChecks[index]) {
        const modifiedCoupon = modifiedCoupons[index] || {};
        const actionType = payChecks[index] ? 'pago' : 'regulariza';

        return {
          uuid: coupon.uuid,
          third_due_amount: modifiedCoupon.third_due_amount || coupon.third_due_amount,
          third_due_date: modifiedCoupon.third_due_date || coupon.third_due_date,
          interest: modifiedCoupon.interest || 0,
          action: actionType,
        };
      }
      return null;
    })
    .filter(coupon => coupon !== null);

  return { coupons };
};

/**
 * Valida si una fecha es válida
 * @param {string} dateString - Fecha en string
 * @returns {boolean} - True si es válida
 */
export const isValidDate = (dateString) => {
  const date = new Date(dateString);
  return date instanceof Date && !isNaN(date.getTime());
};

/**
 * Calcula el total de una lista de cupones
 * @param {Array} coupons - Lista de cupones
 * @param {string} amountField - Campo del monto a sumar
 * @returns {number} - Total calculado
 */
export const calculateCouponsTotal = (coupons, amountField = 'third_due_amount') => {
  if (!Array.isArray(coupons)) return 0;
  
  return coupons.reduce((total, coupon) => {
    const amount = parseFloat(coupon[amountField]) || 0;
    return total + amount;
  }, 0);
};

/**
 * Formatea un número como moneda
 * @param {number} amount - Monto a formatear
 * @returns {string} - Monto formateado
 */
export const formatCurrency = (amount) => formatCurrencyShared(amount);

/**
 * Normaliza un valor numérico
 * @param {any} value - Valor a normalizar
 * @returns {number} - Valor normalizado
 */
export const normalizeNumericValue = (value) => {
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Compara dos fechas
 * @param {string|Date} date1 - Primera fecha
 * @param {string|Date} date2 - Segunda fecha
 * @returns {number} - Diferencia en milisegundos
 */
export const compareDates = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return d1.getTime() - d2.getTime();
};

/**
 * Agrupa cupones por criterio
 * @param {Array} coupons - Lista de cupones
 * @param {Function} groupFn - Función de agrupamiento
 * @returns {Object} - Cupones agrupados
 */
export const groupCoupons = (coupons, groupFn) => {
  if (!Array.isArray(coupons)) return {};
  
  return coupons.reduce((groups, coupon) => {
    const key = groupFn(coupon);
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(coupon);
    return groups;
  }, {});
};
